import api from './api'
import type { LoginRequest, LoginResponse, User, RegisterRequest, Session, TwoFactorStatus, TwoFactorSetupResponse, TwoFactorEnableResponse } from '@/types/auth.types'

export const authService = {
  // Login user
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await api.post('/api/v1/auth/login', credentials)
    return response.data
  },

  // Register new user
  async register(data: RegisterRequest): Promise<void> {
    await api.post('/api/v1/auth/register', data)
  },

  // Get current user profile
  async getCurrentUser(): Promise<User> {
    const response = await api.get('/api/v1/auth/me')
    return response.data
  },

  // Logout (server-side — clears HttpOnly access/refresh/CSRF cookies)
  async logout(): Promise<void> {
    await api.post('/api/v1/auth/logout', {}, { skipAuthRefresh: true })
  },

  // Refresh session via HttpOnly cookie
  async refreshSession(): Promise<void> {
    await api.post('/api/v1/auth/refresh', {}, { skipAuthRefresh: true })
  },

  // ============================================
  // Session Management
  // ============================================

  // Get all active sessions
  async getSessions(): Promise<Session[]> {
    const response = await api.get('/api/v1/auth/sessions')
    return response.data
  },

  // Revoke a specific session
  async revokeSession(sessionId: string): Promise<void> {
    await api.delete(`/api/v1/auth/sessions/${sessionId}`)
  },

  // ============================================
  // Password Management
  // ============================================

  // Change password (authenticated user)
  async changePassword(data: {
    current_password: string
    new_password: string
  }): Promise<void> {
    await api.post('/api/v1/auth/change-password', data)
  },

  // Request password reset
  async requestPasswordReset(email: string): Promise<void> {
    await api.post('/api/v1/auth/forgot-password', { email })
  },

  // Reset password with token
  async resetPassword(data: {
    token: string
    new_password: string
  }): Promise<void> {
    await api.post('/api/v1/auth/reset-password', data)
  },

  async acceptInvite(data: {
    token: string
    password: string
    confirm_password: string
  }): Promise<void> {
    await api.post('/api/v1/auth/accept-invite', data, { skipAuthRefresh: true })
  },

  /** Dev-only open reset (requires ALLOW_OPEN_PASSWORD_RESET on API). */
  async resetPasswordOpen(data: {
    email: string
    new_password: string
    confirm_password: string
  }): Promise<{ message: string }> {
    const response = await api.post('/api/v1/auth/reset-password-open', data)
    return response.data
  },

  // ============================================
  // Email Verification
  // ============================================

  // Verify email with token
  async verifyEmail(token: string): Promise<void> {
    await api.post('/api/v1/auth/verify-email', { token })
  },

  // Resend verification email — authenticated (unverified) users only.
  async resendVerification(): Promise<void> {
    await api.post('/api/v1/auth/resend-verification', undefined, {
      forceAuthRefresh: true,
    })
  },

  // ============================================
  // Two-Factor Authentication
  // ============================================

  // Get 2FA status
  async get2FAStatus(): Promise<TwoFactorStatus> {
    const response = await api.get('/api/v1/auth/2fa/status')
    return response.data
  },

  // Setup 2FA (get QR code)
  async setup2FA(): Promise<TwoFactorSetupResponse> {
    const response = await api.post('/api/v1/auth/2fa/setup')
    return response.data
  },

  // Enable 2FA with verification code
  async enable2FA(code: string): Promise<TwoFactorEnableResponse> {
    const response = await api.post('/api/v1/auth/2fa/enable', { code })
    return response.data
  },

  // Verify 2FA code
  async verify2FA(code: string): Promise<{ verified: boolean; message: string }> {
    const response = await api.post('/api/v1/auth/2fa/verify', { code })
    return response.data
  },

  // Disable 2FA
  async disable2FA(code: string): Promise<void> {
    await api.post('/api/v1/auth/2fa/disable', { code })
  },

  // Regenerate backup codes
  async regenerateBackupCodes(code: string): Promise<{ backup_codes: string[]; message: string }> {
    const response = await api.post('/api/v1/auth/2fa/regenerate-backup-codes', { code })
    return response.data
  },

  // ============================================
  // Profile Management
  // ============================================

  // Update user profile
  async updateProfile(data: {
    username?: string
    email?: string
    display_name?: string
  }): Promise<User> {
    const response = await api.put('/api/v1/profile/update', data)
    return response.data.data || response.data
  },

  // Upload profile picture
  async uploadProfilePicture(file: File): Promise<{ url: string }> {
    const formData = new FormData()
    formData.append('picture', file)
    const response = await api.post('/api/v1/profile/picture', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  }
}
